<template>
  <div
    ref="mapWrapper"
    :class="`${$vuetify.theme.current.dark ? 'bg-dark' : 'bg-white'}`"
    class="w-100 position-relative d-flex flex-column"
    style="overflow-x: hidden"
    @mousedown="$emit('interact')"
  >
    <div class="flex-grow-1 d-flex flex-column relative-position">
      <div ref="mapReference" class="w-100 flex-grow-1" @wheel="$emit('interact')">
        <v-overlay :model-value="showLoading" class="align-center justify-center always-show" persistent contained style="z-index: 998">
          <v-container class="d-flex justify-center align-center h-100 w-100 flex-column">
            <v-progress-circular indeterminate :size="64" :width="6" class="mb-3" />
            <p v-if="clustersLoading">{{ $t("map.loadingClusters") }}</p>
            <p v-else-if="assetsLoading">{{ $t("map.loadingAssets") }}</p>
            <p v-else>{{ $t("map.loadingData") }}</p>
          </v-container>
        </v-overlay>
      </div>

      <template v-if="!hideControls">
        <filter-menu
          v-if="organizationSettings"
          v-model:selected-categories="assetCategories"
          :hide-asset-categories="supplierStore.isLoggedInOnSupplier"
          :organization-settings="organizationSettings!"
        >
          <template #custom-buttons>
            <filter-button icon="landscape" :text="$t('map.type')" :badge="layersSatellite" @click="layersSatellite = !layersSatellite" />
            <filter-button
              icon="person_pin_circle"
              :text="$t('map.position')"
              :badge="trackPosition"
              @click="trackPosition = !trackPosition"
            />
            <filter-button
              v-if="mainAsset || !disableShowNearby"
              :icon="showNearby ? 'explore' : 'explore_off'"
              :text="showNearby ? $t('map.hideNearby') : $t('map.showNearby')"
              @click="showNearby = !showNearby"
            />
            <filter-button icon="my_location" :text="$t('map.recenter')" @click="fitMapToVisibleMarkers" />
          </template>
        </filter-menu>

        <full-screen-control :element="mapWrapper" />

        <div
          v-if="selectedFeatures.length || pdokMapFeatures.length || gisMapFeatures.length"
          class="position-absolute"
          style="top: 10px; right: 10px; max-height: calc(100% - 20px); z-index: 999; overflow: auto"
        >
          <div class="position-sticky top-0 d-flex justify-end" style="z-index: 2; height: 0">
            <v-btn
              class="mt-2 mr-2"
              icon="close"
              variant="elevated"
              density="comfortable"
              :rounded="2"
              @click="
                () => {
                  selectedFeatures = []
                  pdokMapFeatures = []
                  gisMapFeatures = []
                }
              "
            />
          </div>

          <template v-for="(item, index) in selectedFeatures" :key="getFeatureValue<string>(item as FeatureLike, 'id')">
            <asset-info
              v-if="isFeatureAsset(item as FeatureLike)"
              :id="getFeatureValue<string>(item as FeatureLike, 'id')"
              :distance="getFeatureValue<number>(item as FeatureLike, 'distance')"
              :class="{ 'mt-2': index > 0 }"
            />
            <position-info
              v-else-if="isFeaturePosition(item as FeatureLike)"
              :position="getFeatureValue<GeolocationCoordinates>(item as FeatureLike, 'position')"
              :class="{ 'mt-2': index > 0 }"
            />
          </template>

          <pdok-feature-info
            v-if="pdokMapFeatures.length"
            :class="{ 'mt-2': selectedFeatures.length }"
            :pdok-map-features="pdokMapFeatures"
          />

          <gis-feature-info v-if="gisMapFeatures.length" :class="{ 'mt-2': selectedFeatures.length }" :gis-map-features="gisMapFeatures" />
        </div>

        <zoom-control @zoom-in="setZoom(1)" @zoom-out="setZoom(-1)" />
      </template>
    </div>

    <div ref="tooltipRef" class="tooltip" :style="{ display: showTooltip ? 'block' : 'none' }" />
  </div>
</template>

<script setup lang="ts">
import type { MapBrowserEvent } from "ol"
import { Overlay, View } from "ol"
import OlMap from "ol/Map"
import type { FeatureLike } from "ol/Feature"
import TileLayer from "ol/layer/Tile"
import OSM from "ol/source/OSM"
import { fromLonLat } from "ol/proj"
import debounce from "lodash-es/debounce"

enum MarkerType {
  ASSET = "ASSET",
  POSITION = "POSITION",
}

const supplierStore = useSupplierStore()
const organizationSettings = ref<OrganizationSettings | null>(null)

const showNearby = defineModel<boolean>("showNearby", { default: false })
const mapFilter = defineModel<Record<string, FilterQueryValue>>("mapFilters", { default: () => {} })

const pickerCoordinates = defineModel<LatLngAlt>("pickerCoordinates")

const {
  assets = undefined,
  assetsLoading = false,
  clusters = undefined,
  clustersLoading = false,
  disableShowNearby = false,
  loading = false,
  mainAsset = undefined,
  picker = false,
  readOnly = false,
  showMainAssetRelations = false,
  zoom = undefined,
} = defineProps<{
  assets?: Array<GeoAsset>
  assetsLoading?: boolean // Loading of the assets
  clusters?: Array<GeoCluster>
  clustersLoading?: boolean // Loading of the clusters
  disableShowNearby?: boolean
  hideControls?: boolean // disables click events like marker click, drag, etc
  loading?: boolean // Generic loading before fetch map data is ready
  mainAsset?: LookupAsset
  picker?: boolean
  readOnly?: boolean
  showMainAssetRelations?: boolean
  zoom?: number
}>()

const emit = defineEmits<{ (e: "interact"): void }>()

const authStore = useAuthStore()
const organizationStore = useOrganizationStore()

const clusterZoomLevel = 14

const { currentOrganization } = storeToRefs(authStore)

const initialCenterOnLoad = ref(false)
const mapWrapper = ref<HTMLElement | null>(null) // Reference to the map wrapper container element - this also includes the filter menu.
const mapReference = ref<HTMLElement | null>(null) // Reference to the map container element
const tooltipRef = ref<HTMLDivElement | null>(null)
const map: Ref<OlMap | null> = ref(null) // Reactive reference for the OpenLayers map instance
const showTooltip = ref(false)
const refitAfterLoadOfCluster = ref<boolean>(false)
const currentZoom = ref<number>(0)
// Active filters
// Filters applied to all organizations
const layersSatellite = useLocalStorage<boolean>("ol:layer-satellite", false)

const assetShowRelations = useLocalStorage<boolean>("ol:asset-relations", false)
const trackPosition = useLocalStorage<boolean>("ol:position", false)
// Filter specific to organization

const assetCategories = useLocalStorage<Array<string>>(`map:${currentOrganization.value}:asset-categories`, [])

const zoomReference = computed(() => zoom)
const clusterReference = computed(() => clusters)
const assetsReference = computed(() => assets)
const mainAssetReference = computed(() => mainAsset)
const showMainAssetRelationsReference = computed(() => showMainAssetRelations)

const forceInteract = () => {
  emit("interact")
}

const lastView = useSessionStorage<DashboardMapView>("map-last-view-ol", {
  initialized: false,
  coordinates: { lat: 0, lng: 0 },
  zoom: 7,
  selectedFeatures: [],
})

const { bounds, boundsByKey } = useFeatureBounds()

const { removeLayer } = useLayerManagement(map)
const { initializeSelectedFeatures, selectedFeatures, getFeatures } = useSelectedFeatures(map, currentZoom, clusterZoomLevel)
const { restoreLastView, initializeViewPersistence } = useOpenLayerViewPersistence(map, lastView, selectedFeatures)
const { fitMapToVisibleMarkers } = useOpenLayersCenter(
  map,
  clusterZoomLevel,
  bounds,
  forceInteract,
  restoreLastView,
  refitAfterLoadOfCluster,
  zoomReference,
)
const { selectedRelatedAssets, mainRelatedAssets } = useOpenLayersRelations(
  map,
  mainAssetReference,
  selectedFeatures,
  showMainAssetRelationsReference,
  assetShowRelations,
)
const { allAssets } = useAllOpenLayerAssets(assetsReference, mainAssetReference, mainRelatedAssets, selectedRelatedAssets)
const { setConvexHullOnHover, getClusterStyle } = useRenderClusters(
  map,
  initialCenterOnLoad,
  clusterReference,
  clusterZoomLevel,
  boundsByKey,
  fitMapToVisibleMarkers,
  forceInteract,
  refitAfterLoadOfCluster,
)
const { initializeMarkers } = useRenderMarkers(
  map,
  initialCenterOnLoad,
  clusterZoomLevel,
  boundsByKey,
  allAssets,
  mainAssetReference,
  selectedFeatures,
  fitMapToVisibleMarkers,
  refitAfterLoadOfCluster,
  getClusterStyle,
  trackPosition,
)
const { initializeFetchedLayers, pdokMapFeatures, gisMapFeatures, togglePdokLayer } = useFetchedLayers(
  map,
  organizationSettings,
  clusterZoomLevel,
)

const loadingRef = computed(() => loading)
const clusterLoadingRef = computed(() => clustersLoading)
const assetsLoadingRef = computed(() => assetsLoading)
const clusterZoomLevelRef = computed(() => clusterZoomLevel)

const { showLoading } = useShowMapLoading(loadingRef, clusterLoadingRef, assetsLoadingRef, clusterZoomLevelRef, currentZoom)

const usePickerRef = computed(() => picker)
const readOnlyRef = computed(() => readOnly)
const { initializePicker } = useOpenLayersPicker(map, boundsByKey, readOnlyRef, usePickerRef, pickerCoordinates)

watchEffect(() => {
  if (!assetCategories.value.length || supplierStore.isLoggedInOnSupplier) {
    mapFilter.value = {}
    return
  }

  mapFilter.value = {
    "category._id": { $in: assetCategories.value },
  }
})

/** The zoom level from which the clusters are shown. */

const setZoom = (amount: number) => {
  if (!map.value) return

  const view = map.value.getView()
  const zoom = view.getZoom() ?? 7

  view.animate({ zoom: zoom + amount, duration: 250 })
}

const toggleSatelliteLayer = () => {
  if (!layersSatellite.value) {
    removeLayer(OpenLayersLayer.SATELLITE)
    return
  }

  togglePdokLayer("hwh", "luchtfotorgb", OpenLayersLayer.SATELLITE, LayerType.WMTS)
}

const tooltipOverlay = ref<Overlay>()

// Creates the OpenLayers map instance
const initializeMap = () => {
  const view = new View({
    center: fromLonLat([defaultCoords.lng, defaultCoords.lat]),
    zoom: 7,
    maxZoom: 20,
  })

  const osm = new TileLayer({
    source: new OSM({
      attributions: [], // Remove OpenStreetMap attributions
      maxZoom: 18,
      wrapX: false,
    }),
  })

  osm.set("name", OpenLayersLayer.BASE)

  map.value = new OlMap({
    controls: [],
    target: mapReference.value!, // Target the map container
    layers: [osm],
    view,
  })

  initializeLayers()
  initializeSelectedFeatures()
  initializeViewPersistence()
  initializePicker()

  // Create overlay for tooltip
  tooltipOverlay.value = new Overlay({
    element: tooltipRef.value as HTMLElement,
    offset: [10, 0],
    positioning: "bottom-left",
    stopEvent: false,
  })

  map.value.addOverlay(tooltipOverlay.value)

  view.on("change:resolution", () => {
    currentZoom.value = view.getZoom() ?? 0
  })

  // @ts-expect-error "pointermove" is not a ts recognized event but exists regardless
  map.value.on("pointermove", debounce(onPointerMove, 10))

  toggleInteractions()
  if (picker || mainAsset) {
    fitMapToVisibleMarkers()
  }
}

const toggleInteractions = () => {
  if (!map.value) return

  map.value.getInteractions().forEach((interaction) => {
    interaction.setActive(!readOnly)
  })
}

const onPointerMove = (event: MapBrowserEvent<PointerEvent>) => {
  const features = getFeatures(event.pixel)

  setConvexHullOnHover(features)

  if (!features.length) {
    document.body.style.cursor = "default"
    showTooltip.value = false
    return
  }

  document.body.style.cursor = "pointer"

  tooltipRef.value!.innerHTML = features[0]!.get("name") as string
  const coordinate = event.coordinate
  tooltipOverlay.value?.setPosition(coordinate)

  showTooltip.value = true
}

const initializeLayers = async () => {
  if (!map.value) return

  toggleSatelliteLayer()

  initializeMarkers()

  if (!organizationSettings.value) {
    organizationSettings.value = await organizationStore.getSettings(currentOrganization.value!)
  }

  initializeFetchedLayers()
}

const isFeatureAsset = (feature: FeatureLike) => feature.get("type") === MarkerType.ASSET
const isFeaturePosition = (feature: FeatureLike) => feature.get("type") === MarkerType.POSITION
const getFeatureValue = <T,>(feature: FeatureLike, value: string) => feature.get(value) as T

// Runs when the map container element is available
watchOnce(mapReference, () => {
  if (!mapReference.value) return
  initializeMap()
})
watch(layersSatellite, () => toggleSatelliteLayer())

watch(
  () => readOnly,
  () => {
    toggleInteractions()
  },
)

defineExpose({
  recenter: fitMapToVisibleMarkers,
  initialCenterOnLoad,
})
</script>

<style scoped lang="scss">
.tooltip {
  position: absolute;
  background-color: #3d3d3d;
  color: white;
  margin-left: 5px;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: 4px;
  white-space: nowrap;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.3);
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
  opacity: 0.95;
  pointer-events: none;
  z-index: 1000;
}
</style>
